import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IColunaExtracao, IExtensaoArquivo, ILayoutExtracaoTable } from "../../../models";
import { IFormError, IFormProps } from "../../utils";

interface ICreateLayoutExtracaoFormFields {
    Nome: string
    Colunas: IColunaExtracao[]
}

interface IEditLayoutExtracaoFormFields {
    IdVelesGeoLayoutExtracao: number
    Nome: string
    Colunas: IColunaExtracao[]
}

interface ICreateLayoutPadraoExtracaoFormFields {
    Nome: string
}

interface ILayoutExtracaoState {
    recortes: string
    layouts: ILayoutExtracaoTable[]
    layoutSelecionado?: ILayoutExtracaoTable
    extensaoArquivoSelecionada?: IExtensaoArquivo
    createLayoutForm: IFormProps<ICreateLayoutExtracaoFormFields>
    createLayoutFormOpen: boolean
    editLayoutForm: IFormProps<IEditLayoutExtracaoFormFields>
    editLayoutFormOpen: boolean
    openDeleteLayout: boolean
    openCreateLayoutPadraoForm: boolean
    createLayoutPadraoForm: IFormProps<ICreateLayoutPadraoExtracaoFormFields>
}

const createLayoutExtracaoFormFieldsInitialState: ICreateLayoutExtracaoFormFields = {
    Colunas: [],
    Nome: ''
}

const editLayoutExtracaoFormFieldsInitialState: IEditLayoutExtracaoFormFields = {
    Colunas: [],
    IdVelesGeoLayoutExtracao: 0,
    Nome: ''
}

const createLayoutPadraoExtracaoFormFieldsInitialState: ICreateLayoutPadraoExtracaoFormFields = {
    Nome: ''
}

const initialState: ILayoutExtracaoState = {
    recortes: '',
    layouts: [],
    createLayoutForm: {
        errors: [],
        fields: createLayoutExtracaoFormFieldsInitialState,
        touched: false
    },
    createLayoutFormOpen: false,
    editLayoutForm: {
        errors: [],
        fields: editLayoutExtracaoFormFieldsInitialState,
        touched: false
    },
    editLayoutFormOpen: false,
    openDeleteLayout: false,
    openCreateLayoutPadraoForm: false,
    createLayoutPadraoForm: {
        errors: [],
        fields: createLayoutPadraoExtracaoFormFieldsInitialState,
        touched: false
    }
}

export const validateCreateLayoutExtracaoForm = (fields: ICreateLayoutExtracaoFormFields): IFormError<keyof ICreateLayoutExtracaoFormFields>[] => {
    const errors: IFormError<keyof ICreateLayoutExtracaoFormFields>[] = [];

    if (!fields.Nome) {
        errors.push({
            key: 'Nome',
            message: 'O nome do layout não pode ficar em branco',
        });
    } else if (fields.Nome.length === 0 || fields.Nome.replace('  ', ' ').trim().length === 0) {
        errors.push({
            key: 'Nome',
            message: 'O nome do layout não pode ficar em branco'
        })
    } else if (fields.Colunas.length === 0) {
        errors.push({
            key: 'Colunas',
            message: 'Selecione ao menos uma coluna'
        })
    }

    return errors;
}

export const validateEditLayoutExtracaoForm = (fields: IEditLayoutExtracaoFormFields): IFormError<keyof IEditLayoutExtracaoFormFields>[] => {
    const errors: IFormError<keyof IEditLayoutExtracaoFormFields>[] = [];

    if (!fields.Nome) {
        errors.push({
            key: 'Nome',
            message: 'Nome é obrigatório',
        });
    } else if (fields.Nome.length === 0 || fields.Nome.replace('  ', ' ').trim().length === 0) {
        errors.push({
            key: 'Nome',
            message: 'O nome do layout não pode ficar em branco'
        })
    } else if (fields.Colunas.length === 0) {
        errors.push({
            key: 'Colunas',
            message: 'Selecione ao menos uma coluna'
        })
    } else if (fields.IdVelesGeoLayoutExtracao === 0) {
        errors.push({
            key: 'IdVelesGeoLayoutExtracao',
            message: 'É necessário o id do layout'
        })
    }

    return errors;
}

export const validateCreateLayoutPadraoExtracaoForm = (fields: ICreateLayoutPadraoExtracaoFormFields): IFormError<keyof ICreateLayoutPadraoExtracaoFormFields>[] => {
    const errors: IFormError<keyof ICreateLayoutPadraoExtracaoFormFields>[] = [];

    if (!fields.Nome) {
        errors.push({
            key: 'Nome',
            message: 'O nome do layout não pode ficar em branco',
        });
    } else if (fields.Nome.length === 0 || fields.Nome.replace('  ', ' ').trim().length === 0) {
        errors.push({
            key: 'Nome',
            message: 'O nome do layout não pode ficar em branco'
        })
    }

    return errors;
}

const layoutExtracaoSlice = createSlice({
    name: 'layoutExtracao',
    initialState,
    reducers: {
        setLayoutsState(state, action: PayloadAction<ILayoutExtracaoTable[]>) {
            state.layouts = action.payload
        },
        resetLayoutsState(state) {
            state.layouts = initialState.layouts
        },
        setLayoutSelecionadoState(state, action: PayloadAction<ILayoutExtracaoTable>) {
            state.layoutSelecionado = action.payload
        },
        resetLayoutSelecionadoState(state) {
            state.layoutSelecionado = initialState.layoutSelecionado
        },
        setCreateLayoutFormFieldsNomeState(state, action: PayloadAction<string>) {
            state.createLayoutForm.fields.Nome = action.payload
        },
        setCreateLayoutFormFieldsColunasState(state, action: PayloadAction<IColunaExtracao[]>) {
            state.createLayoutForm.fields.Colunas = action.payload
        },
        setCreateLayoutExtracaoFormTouchedState(state, action: PayloadAction<boolean>) {
            state.createLayoutForm.touched = action.payload
        },
        setCreateLayoutExtracaoFormErrorsState(state, action: PayloadAction<{ errors: IFormError<keyof ICreateLayoutExtracaoFormFields>[] }>) {
            state.createLayoutForm.errors = action.payload.errors;
        },
        setCreateLayoutExtracaoFormDeliveredState(state, action: PayloadAction<boolean>) {
            state.createLayoutForm.delivered = action.payload
        },
        resetCreateLayoutFormState(state) {
            state.createLayoutForm = initialState.createLayoutForm
        },
        setEditLayoutFormFieldsIdVelesGeoLayoutExtracaoState(state, action: PayloadAction<number>) {
            state.editLayoutForm.fields.IdVelesGeoLayoutExtracao = action.payload
        },
        setEditLayoutFormFieldsNomeState(state, action: PayloadAction<string>) {
            state.editLayoutForm.fields.Nome = action.payload
        },
        setEditLayoutFormFieldsColunasState(state, action: PayloadAction<IColunaExtracao[]>) {
            state.editLayoutForm.fields.Colunas = action.payload
        },
        setEditLayoutExtracaoFormTouchedState(state, action: PayloadAction<boolean>) {
            state.editLayoutForm.touched = action.payload
        },
        setEditLayoutExtracaoFormErrorsState(state, action: PayloadAction<{ errors: IFormError<keyof IEditLayoutExtracaoFormFields>[] }>) {
            state.editLayoutForm.errors = action.payload.errors;
        },
        setEditLayoutExtracaoFormDeliveredState(state, action: PayloadAction<boolean>) {
            state.editLayoutForm.delivered = action.payload
        },
        resetEditLayoutFormState(state) {
            state.editLayoutForm = initialState.editLayoutForm
        },
        setCreateLayoutPadraoFormNomeState(state, action: PayloadAction<string>) {
            state.createLayoutPadraoForm.fields.Nome = action.payload
        },
        setCreateLayoutPadraoExtracaoFormTouchedState(state, action: PayloadAction<boolean>) {
            state.createLayoutPadraoForm.touched = action.payload
        },
        setCreateLayoutPadraoExtracaoFormErrorsState(state, action: PayloadAction<{ errors: IFormError<keyof ICreateLayoutPadraoExtracaoFormFields>[] }>) {
            state.createLayoutPadraoForm.errors = action.payload.errors;
        },
        setCreateLayoutPadraoExtracaoFormDeliveredState(state, action: PayloadAction<boolean>) {
            state.createLayoutPadraoForm.delivered = action.payload
        },
        resetCreateLayoutPadraoFormState(state) {
            state.createLayoutPadraoForm = initialState.createLayoutPadraoForm
        },
        setExtensaoArquivoSelecionadaState(state, action: PayloadAction<IExtensaoArquivo>) {
            state.extensaoArquivoSelecionada = action.payload
        },
        resetExtensaoArquivoSelecionadaState(state) {
            state.extensaoArquivoSelecionada = initialState.extensaoArquivoSelecionada
        },
        setCreateLayoutFormOpenState(state, action: PayloadAction<boolean>) {
            state.createLayoutFormOpen = action.payload
        },
        setEditLayoutFormOpenState(state, action: PayloadAction<boolean>) {
            state.editLayoutFormOpen = action.payload
        },
        setOpenDeleteLayoutState(state, action: PayloadAction<boolean>) {
            state.openDeleteLayout = action.payload
        },
        setOpenCreateLayoutPadraoFormState(state, action: PayloadAction<boolean>) {
            state.openCreateLayoutPadraoForm = action.payload
        }
    }
})

export const {
    resetCreateLayoutFormState,
    resetEditLayoutFormState,
    resetLayoutSelecionadoState,
    resetLayoutsState,
    setCreateLayoutExtracaoFormDeliveredState,
    setCreateLayoutExtracaoFormErrorsState,
    setCreateLayoutExtracaoFormTouchedState,
    setCreateLayoutFormFieldsColunasState,
    setCreateLayoutFormFieldsNomeState,
    setEditLayoutExtracaoFormDeliveredState,
    setEditLayoutExtracaoFormErrorsState,
    setEditLayoutExtracaoFormTouchedState,
    setEditLayoutFormFieldsColunasState,
    setEditLayoutFormFieldsIdVelesGeoLayoutExtracaoState,
    setEditLayoutFormFieldsNomeState,
    setLayoutSelecionadoState,
    setLayoutsState,
    resetExtensaoArquivoSelecionadaState,
    setExtensaoArquivoSelecionadaState,
    setCreateLayoutFormOpenState,
    setEditLayoutFormOpenState,
    setOpenDeleteLayoutState,
    resetCreateLayoutPadraoFormState,
    setCreateLayoutPadraoExtracaoFormDeliveredState,
    setCreateLayoutPadraoExtracaoFormErrorsState,
    setCreateLayoutPadraoExtracaoFormTouchedState,
    setCreateLayoutPadraoFormNomeState,
    setOpenCreateLayoutPadraoFormState
} = layoutExtracaoSlice.actions

export const layoutExtracaoReducer = layoutExtracaoSlice.reducer